import React, { useEffect, useState } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Paper,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import { makeStyles } from "@material-ui/core";
import Icon from "../../assets/img/icon.png";
import Banner from "../../assets/img/tagitstore_banner-01.jpg";
import authApi from "../../services/auth";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import { showSnackbar } from "../../redux/snackbar/actions";
import { encryptAES } from "../../utils";

const useStyles = makeStyles((theme) => ({
  btn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  bannerImage: {
    backgroundSize: "cover",
    backgroundPosition: "right",
    [theme.breakpoints.up("md")]: {
      backgroundImage: `url(${Banner})`,
      backgroundPosition: "left",
      backgroundSize: "cover",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  bg: {
    backgroundColor: "#f7f4f2",
  },
}));

function Copyright(props) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      {...props}
    >
      {"Copyright © "}
      <Link color="inherit" href="" sx={{ textDecoration: "none" }}>
        TAGITStore
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

const ForgotPassword = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let lastSubmitTime = 0;
  const [userInteracted, setUserInteracted] = useState(false);

  useEffect(() => {
    const handleUserInteraction = () => setUserInteracted(true);

    // document.addEventListener("mousemove", handleUserInteraction);
    document.addEventListener("keydown", handleUserInteraction);
    document.addEventListener("scroll", handleUserInteraction);
    document.addEventListener("touchstart", handleUserInteraction);

    return () => {
      // document.removeEventListener("mousemove", handleUserInteraction);
      document.removeEventListener("keydown", handleUserInteraction);
      document.removeEventListener("scroll", handleUserInteraction);
      document.removeEventListener("touchstart", handleUserInteraction);
    };
  }, []);

  const isBotDetected = () => {
    // Skip bot detection for mobile devices
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        return false;
    }

    // Check for common bot indicators only on desktop
    return (
        (navigator.webdriver && !/Android|iPhone/i.test(navigator.userAgent)) ||
        (!navigator.languages || navigator.languages.length === 0) ||
        (/bot|crawler|spider|headless|phantom/i.test(navigator.userAgent) && 
         !/Chrome|Firefox|Safari/i.test(navigator.userAgent)) ||
        /HeadlessChrome|puppeteer|phantomjs/i.test(navigator.userAgent) ||
        (!userInteracted && !/Android|iPhone/i.test(navigator.userAgent))
    );
};

  const handleSubmit = (formData) => {
    const now = Date.now();

    if (isBotDetected()) {
      alert("Bot access is restricted!");
      window.location.reload();
      return;
    }

    if (now - lastSubmitTime < 3000) {
      alert("Multiple rapid submissions detected!");
      window.location.reload();
      return;
    }

    lastSubmitTime = now;

    if (formData?.honeypot) {
      alert("Suspicious activity detected!");
      return;
    }
    const data = {
      email: formData?.email?.toLowerCase(),
    };
    const encryptedData = encryptAES(data);
    dispatch(showLoader());
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.msg || "Info updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik.resetForm();
        navigate("/");
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.msg || "Invalid Credentials",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    authApi.forgotPassword(encryptedData).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      email: yup.string().email("Invalid email").required("Email required"),
    }),
    onSubmit: (values) => handleSubmit(values),
  });
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      formik.handleSubmit();
    }
  };
  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline />
      <Grid item xs={false} sm={null} md={8} className={classes.bannerImage} />
      <Grid
        item
        xs={12}
        sm={12}
        md={4}
        pt={5}
        component={Paper}
        elevation={6}
        square
        className={classes.bg}
      >
        <Box
          sx={{
            my: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={Icon}
            alt="logo"
            sx={{
              my: 2,
              width: "75px",
              height: "auto",
              maxWidth: "100%",
            }}
          />
          <Typography component="h1" variant="h5">
            Forgot Password
          </Typography>
          <Box sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              onKeyDown={handleKeyDown}
              name="email"
              type="text"
              autoComplete="off"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <Button
              variant="contained"
              fullWidth
              sx={{
                height: "100%",
                background: "linear-gradient(45deg, #ff4b2b 30%, #ff416c 90%)",
                color: "#fff",
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
                textTransform: "uppercase",
                fontWeight: "bold",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #ff416c 30%, #ff4b2b 90%)",
                  boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.3)",
                },
              }}
              onClick={formik.handleSubmit}
            >
              Send Reset Link
            </Button>
            <Grid container mt={1}>
              <Grid item xs={12} sm={6} md={6}>
                <Link
                  href="/"
                  variant="body2"
                  sx={{ textDecoration: "none", color: "#9d9d9d" }}
                >
                  Back to Sign In
                </Link>
              </Grid>
            </Grid>
            <Copyright sx={{ mt: 10 }} />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
